import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { AppointmentBookingSource, AppointmentType, Department, IAvailability, IAvailabilityOptions, IEmployee, IGeneralSettings, IHoliday, ILanguage, ITimeSlot, TransportationType } from '@signal/asp-data-commons';
import { SubscriptionList } from '../../../shared/models/asp.types';
import { assetUrl, unsubscribeSubscriptions, globalTXM } from '../../../shared/services/util.service';
import { ServiceAppointmentState } from '../../../store/service-appointment/service-appointment.reducer';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { LoadAvailability, UpdateCustomerFormTransport } from '../../../store/service-appointment/service-appointment.actions';
import { IAvail, SalesAppointmentState } from 'src/app/modules/store/sales-appointment/sales-appointment.reducer';
import * as fromActions from '../../../store/sales-appointment/sales-appointment.actions';
import { PartsAppointmentState } from 'src/app/modules/store/parts-appointment/parts-appointment.reducer';
import * as fromPartsActions from '../../../store/parts-appointment/parts-appointment.actions';
import * as fromServiceSelectors from '../../../store/service-appointment/service-appointment.selectors';
import * as fromSalesSelectors from '../../../store/sales-appointment/sales-appointment.selectors';
import * as fromPartsSelectors from '../../../store/parts-appointment/parts-appointment.selectors';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { SettingsState } from 'src/app/modules/store/settings/settings.reducer';
import { selectSettings } from 'src/app/modules/store/settings/settings.selectors';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { languages } from 'src/assets/data/languages';
import { DatePipe } from '@angular/common';
import { selectHolidays } from 'src/app/modules/store/holiday/holiday.adapter';
import { HolidayState } from 'src/app/modules/store/holiday/holiday.reducer';
import { Observable } from 'rxjs';
import { AddLeavesService } from '../../services/add-leaves.service';
import * as HolidayActions from '../../../store/holiday/holiday.actions';
import { AvailabilityService } from '../../services/availability.service';

export interface AvailabilityFormValues {
  advisorId: string;
  fniAdvisorId?: string;
  languageId: string;
  timeSlotId: string;
  appointmentStartDate: string;
}

@Component({
  selector: 'app-availability',
  templateUrl: './availability.component.html',
  styleUrls: ['./availability.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AvailabilityComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AvailabilityComponent),
      multi: true
    },
    DatePipe
  ]
})

export class AvailabilityComponent implements ControlValueAccessor, OnDestroy, OnInit, OnChanges {

  @Input() options: IAvailabilityOptions;
  @Input() availability: IAvailability;
  @Input() source: string;
  @Input() isAvailabilityLoading: boolean;
  @Input() selectedTransport: string;
  @Output() monthChanged: EventEmitter<DateTime> = new EventEmitter();
  @Output() timeSlotValidator: EventEmitter<boolean> = new EventEmitter();
  @Output() formEdited:EventEmitter<boolean> = new EventEmitter();
  @Input() vin: string;
  @Input() appointmentId: string;
  @Input() apptType: string;
  @Input() showClearDateTimeBtn: boolean;
  @Input() checklistDuration: { hr: number; min: number; };
  @Input() disableCalendar: boolean;
  @Input() submitAllowed: boolean;
  @Input() data;
  @Input() calenderMessage='';

  isTimeSlotDisabled: boolean;
  form: UntypedFormGroup;
  subscriptions: SubscriptionList = {};
  availableDates: string[] = [];
  closedDates = [];
  currentMonth: DateTime;

  advisors: IEmployee[] = [];
  fniAdvisors: IEmployee[] = [];
  dealerAdvisors: IEmployee[] = [];
  currTransportOption:string;
  timeSlots: ITimeSlot[] = [];
  selected: string = '';
  oldValue: any = {};
  exludedForTransportCheck = [TransportationType.WAITER, TransportationType.REMOTE];
  dateFormat : string = 'yyyy-MM-dd';
  settings: IGeneralSettings;
  weekdays = [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  private readonly confirmConfig = {
    width: '550px',
    panelClass: 'asp-modal-container',
    disableClose:true,
    data: {popupTitle : 'otherTexts.appointmentOverrideConfirmation', popupBodyText: 'otherTexts.appointmentOverrideConfirmationMsg',clicked:true, 
    showAlert: true,
    saveButton: 'service.yes', cancelButton: 'service.no', overrideSelectedDate:''}
  };
  languages:ILanguage[]=[];
  checkIfLanguageAvailalble:any;
  @Input() isEditMode=false;
  @Input() customerLanguage='';
  @Input() apptAdvisor = '';
  holidays$: Observable<IHoliday[]>;
  override = {allow: false, date: ''};
  timerid;
  apptLanguage = '';
  dealerSettings$: Observable<any>;
  allTransportation: any;
  validTransport: any;
  transportSelected;
  trasportationChange=false;
  partsApptType = AppointmentType.PARTS;

  get value(): AvailabilityFormValues {
    return this.form?.value;
  }

  set value(value: AvailabilityFormValues) {
    const normalizedValue = this.normalizeAvailabilityValue(value);
    this.form.setValue(normalizedValue);
    this.selected = normalizedValue.advisorId;
    this.onChange(normalizedValue);
    this.onTouched();
    if(this.disableCalendar){
      this.form.get('appointmentStartDate').patchValue("");
      this.form.get('timeSlotId').patchValue("");
    }
  }

  constructor(private readonly formBuilder: UntypedFormBuilder,
    private readonly serviceAppointmentStore: Store<ServiceAppointmentState>,
    private readonly salesAppointmentStore: Store<SalesAppointmentState>,
    private readonly partsAppointmentStore: Store<PartsAppointmentState>,
    private readonly settingsState: Store<SettingsState>,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly holidayState: Store<HolidayState>,
    public addLeaveService: AddLeavesService,
    public availabilityService: AvailabilityService) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      timeSlotId: ['', [Validators.required]],
      advisorId: ['', [Validators.required]],
      fniAdvisorId: ['-1'],
      languageId: ['', [Validators.required]],
      appointmentStartDate: ['', [Validators.required]]
    });
    this.advisors = this.getSalesAdvisorOptions();
    this.fniAdvisors = this.getFniAdvisorOptions();
    this.subscriptions.form = this.serviceAppointmentStore.select(fromServiceSelectors.selectAppointmentForm).subscribe((data:any) => {
      const selectedServices = data.serviceForm.selectedServices || [];
      if(selectedServices && selectedServices.length){
        const languageId = data.availabilityForm?.availability?.languageId;
        this.selectedServiceHandle(selectedServices,languageId)
      }
    });
    this.checkIfLanguageAssigned();
    this.subscriptions.settings = this.settingsState.select(selectSettings).subscribe(data => {
      this.settings = data;
      if(this.data) {
        this.allTransportation = data?.transport?.transportOptions
        this.validTransport = this.allTransportation?.filter(t => t.isActive);
        this.transportSelected = this.data?.transportation?.id || TransportationType.WAITER;
      }
    });

    if(this.options.department !== Department.SERVICE) {
      const holidayDepartment = this.apptType === AppointmentType.PARTS ? Department.PARTS : Department.SALES;
      const holObj = {
        activeYear:new Date().getFullYear().toString(),
        department: holidayDepartment,
      }
      this.holidayState.dispatch(new HolidayActions.LoadHolidays(holObj));
      this.holidays$ = this.holidayState.select(selectHolidays);
      this.setHolidays();
    }

    this.setFormSubscription();
  }

  getTranslation(value){
    if (value === 'Remote' && this.data.appointmentType === AppointmentType.SERVICE) {
        value = 'RemoteService';
       return  this.translate.instant(`common.${value}`)
      }
      else {
        if(value === "Rideshare" && this.translate.currentLang.split("-")[1] === "MX"){
            return this.translate.instant(`service.complimentaryTransportation`)
          }
        return this.translate.instant(`common.${value}`)
      }
  }

  onSelectingPickupLocation(value){
    this.transportSelected = value;
    this.trasportationChange = true;
    this.availabilityService.updateData({data: this.transportSelected, changed: true});
    if(this.data.appointmentType === AppointmentType.SERVICE){
        this.serviceAppointmentStore.dispatch(new UpdateCustomerFormTransport({transportData: this.transportSelected}))
    } else if (this.data.appointmentType === AppointmentType.PARTS) {
      this.partsAppointmentStore.dispatch(new fromPartsActions.UpdateCustomerFormTransport({ transportData: this.transportSelected }))
    }else if(this.data.appointmentType === AppointmentType.TEST_DRIVE || this.data.appointmentType === AppointmentType.DELIVERY) {
        this.salesAppointmentStore.dispatch(new fromActions.UpdateCustomerFormTransport({transportData: this.transportSelected}))
    }
}

  private selectAppointmentDetailsStore(): Observable<any> {
    return this.apptType === AppointmentType.PARTS
      ? this.partsAppointmentStore.select(fromPartsSelectors.appointmentDetails)
      : this.salesAppointmentStore.select(fromSalesSelectors.appointmentDetails);
  }

  private dispatchSalesLikeAvailability(payload: any) {
    if (this.apptType === AppointmentType.PARTS) {
      this.partsAppointmentStore.dispatch(new fromPartsActions.LoadAvailability(payload));
      return;
    }
    this.salesAppointmentStore.dispatch(new fromActions.LoadAvailability(payload));
  }

  selectedServiceHandle(selectedServices,languageId){
    const txmOpcodes = selectedServices.filter(service => service?.details?.isTXM)
        if(txmOpcodes && txmOpcodes.length && txmOpcodes.length === selectedServices.length && globalTXM){
          this.advisors = (this.isEditMode && (this.languages.length > this.options?.languages?.length) && languageId === this.customerLanguage) ? this.options?.advisors : this.options.advisors.filter(a => (a.isTxm || a.id === '-1') && (a.languages && a.languages.some(l => l.languageId === languageId)))
        }else{
          this.advisors = (this.isEditMode && (this.languages.length > this.options?.languages?.length) && languageId === this.customerLanguage) ? this.options?.advisors : this.options.advisors.filter(a => a.languages && a.languages.some(l => l.languageId === languageId));
        }
  }

  ngOnDestroy() {
    this.addLeaveService.resetValues();
    this.addLeaveService.leaveDetailsYearWise = [];
    this.addLeaveService.recurrentLeaveList = [];
    this.addLeaveService.dealerShipHoliday = [];
    unsubscribeSubscriptions(this.subscriptions);
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  registerOnChange(fn) {
    this.onChange = fn;
  }

  /* check if language coming from appt is assinged to the consultant or add otherwise if its an edit mode*/
  checkIfLanguageAssigned()
  {
    if(this.customerLanguage!=='' && this.languages.length && this.isEditMode)
    {
      this.apptLanguage = this.customerLanguage;
      const checkIfLanguageAvailalble = this.languages?.find(l => l.languageId === this.customerLanguage);
      if (!checkIfLanguageAvailalble) {
        const language = languages?.find(l => l.languageId === this.customerLanguage)?.language;
        this.languages.push({ languageId: this.customerLanguage, language });
      }
      else if(checkIfLanguageAvailalble && this.apptAdvisor !== '' && this.options?.advisors?.length) {
        const isAdvisorLanguageAvailable = this.options?.advisors?.find(x=> x.id === this.apptAdvisor && x.languages.some(l=> l.languageId === this.customerLanguage ) );
        if(!isAdvisorLanguageAvailable) {
        const language = languages?.find(l => l.languageId === this.customerLanguage)?.language;
        const advisor =  this.options.advisors?.find(x=> x.id === this.apptAdvisor);
        const advisorsExAppt = this.options.advisors?.filter(x=> x.id !== this.apptAdvisor) || [];
          if (advisor) {
            this.options = { ...this.options, advisors: [...advisorsExAppt, { ...advisor, languages: [...(advisor?.languages || []), { languageId: this.customerLanguage, language }] }] }
          }
          else {
            this.options = { ...this.options, advisors: advisorsExAppt }
          }
        }
      }
    }
  }

  errorLoadImage(e, i) {
    if (this.advisors[i]?.avatar != null) {
      // this.advisors[i].avatar=''
    }
  }

  setDefaultImg(event, val) {
    event.target.onerror = null;
    event.target.src = assetUrl(val);
  }

  getProfileText(name) {
    const i = name?.split(' ');
    let retVal = '';
    i?.forEach(element => {
      retVal += element?.charAt(0);
    });
    return retVal;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }

    this.availableDates = this.availability.availableDates?.filter(a => a.isOpen).map(a => a.date);
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_: UntypedFormControl) {
    return this.form.valid ? null : { availability: { valid: false } };
  }

  onSuggestedTimesSelected(date: string, timeSlotId: string) {
    this.form.get('appointmentStartDate').patchValue(date);
    this.form.get('timeSlotId').patchValue(timeSlotId);
    this.timeSlotValidator.emit(false);
  }

  onRemoveTimesSelected() {
    this.form.get('timeSlotId').patchValue("");
    this.form.get('appointmentStartDate').patchValue("");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty(('availability'))) {
      if(this.apptType !== AppointmentType.SERVICE){
       this.handleOnchangeAvailabilitySales()
      }
      this.availableDates = changes.availability.currentValue.availableDates?.filter(a => a.isOpen).map(a => a.date);

      this.setOverrideTimeSlot();

      if (this.form) {
        this.timeSlots = this.availability.availableDates
          ?.find(a => a.date === this.form.value.appointmentStartDate)
          ?.timeSlots;
        this.setDisabledTimeSlot();
      }
    }

    if (changes.hasOwnProperty('options')) {
      this.handleOnchangeOptionLogic(changes)
    }

    if (changes.hasOwnProperty('selectedTransport') && !changes.selectedTransport.firstChange && !!changes.selectedTransport.previousValue) {
      this.currTransportOption = changes.selectedTransport.currentValue
      if(this.apptType !== AppointmentType.SERVICE){
        this.handleOnchangeTransportSales()
      }
      this.checkAvailabilityByTransport();
    }
  }

  handleOnchangeOptionLogic(changes){
    const countryCode = this.translate?.currentLang?.split("-")[1];
      let defaultLanguage = "";
      if(countryCode === "US"){
        defaultLanguage = "en"
      }else{
        defaultLanguage = "es"
      }
      /* we need to populate options.languages in the languages array for the first
      time only */
      if( changes?.options?.currentValue?.languages?.length && !this.languages.length)
      {
        this.languages = [...changes.options.currentValue.languages];
        this.checkIfLanguageAssigned();
      }
      this.advisors = this.getSalesAdvisorOptions(defaultLanguage);
      this.fniAdvisors = this.getFniAdvisorOptions();
      if(this.apptType !== AppointmentType.SERVICE){
        this.handleOnchangeOptionsSales()
      }
  }

  handleOnchangeAvailabilitySales(){
    this.selectAppointmentDetailsStore().subscribe(appt => {
      const languageId:any = appt?.availability?.languageId;
      if(appt.transportationTypeCode === TransportationType.REMOTE){
        this.advisors = this.getSalesAdvisorOptions(languageId, true)
      }else{
        this.advisors = this.getSalesAdvisorOptions(languageId)
      }
    }, () => {})
  }

  handleOnchangeOptionsSales(){
    this.dealerAdvisors = this.advisors
    this.selectAppointmentDetailsStore().subscribe(data => {
        if(data.transportationTypeCode === TransportationType.REMOTE){
          this.advisors = this.advisors?.filter(data => data?.remoteConsultant || data?.id === '-1')
        }
      }, () => {})
  }

  handleOnchangeTransportSales(){
    if(this.currTransportOption === TransportationType.REMOTE){
      this.advisors = this.getSalesAdvisorOptions(this.form?.value?.languageId, true)
    }else{
      this.advisors = this.dealerAdvisors
    }
  }
  
  setOverrideTimeSlot() {
    if(this.override.allow) {
      this.timeSlots = this.availability.availableDates
        ?.find(a => a.date === this.override.date)
        ?.timeSlots;
    }
  }

  private checkAvailabilityByTransport() {
    if (this.options.department == Department.SERVICE
      && this.selectedTransport
      // && this.exludedForTransportCheck.findIndex(x => x == this.selectedTransport) == -1
    ) {
      const formDate = this.form?.value?.appointmentStartDate;
      const advisorId = this.form?.value?.advisorId;
      const vin = this.vin;
      const appointmentId = this.appointmentId;
      if (formDate && advisorId) {
        const date = DateTime.fromISO(formDate)
          .startOf('month')
          .toFormat(this.dateFormat);
        this.serviceAppointmentStore.dispatch(new LoadAvailability(
          { date, advisorId, transport: this.selectedTransport, vin, appointmentId }));
      } else {
        this.serviceAppointmentStore.dispatch(new LoadAvailability(
          { date: "", advisorId: '-1', transport: this.selectedTransport, vin, appointmentId }));
      }
    }
    /* *future implementation sales tranportation type availability call. */
  }

  setHolidays() {
    this.subscriptions.holiday = this.holidays$.subscribe(dealershipholidaylist => {
      if (dealershipholidaylist && dealershipholidaylist.length > 0) {
        for (const holiday of dealershipholidaylist) {
          this.addLeaveService.dealerShipHoliday.push({
            date: holiday.holidayDate,
            dayType: holiday.dayType,
            dayTime: holiday.dayTime
          });
        }
      }
    })
  }

  private setFormSubscription() {
    this.subscriptions.formSub = this.form.valueChanges.subscribe(value => {
      if(_.isEqual(this.oldValue, value)) {
        return;
      }
      // any time the inner form changes update the parent of any change
      this.onChange(value);
      this.onTouched();

      if (this.oldValue.languageId !== value.languageId) {
        /*if its an edit mode and appt language is not assigned to the consultant, populate consultants as it is without filtering */
        this.advisors = (this.isEditMode && this.languages.length > this.options?.languages?.length && value.languageId === this.apptLanguage)
          ? this.getSalesAdvisorOptions()
          : this.getSalesAdvisorOptions(value.languageId);
        if(this.apptType !== AppointmentType.SERVICE){
          this.selectAppointmentDetailsStore().subscribe(data => {
            if(data.transportationTypeCode === TransportationType.REMOTE){
              this.advisors = this.getSalesAdvisorOptions(value.languageId, true)
            }
          }, () => {})
        }
        if(this.apptType === AppointmentType.SERVICE && this.advisors?.length && !(this.isEditMode && this.languages.length > this.options?.languages?.length && value.languageId === this.apptLanguage)) {
          this.updateAdvisorOnLanguageChange(this.advisors, value);
        }
      }

      if (this.oldValue.appointmentStartDate !== value.appointmentStartDate) {
        this.timeSlots = this.availability.availableDates
          ?.find(a => a.date === value.appointmentStartDate)
          ?.timeSlots;
        this.setDisabledTimeSlot();
      }

      if(this.oldValue.appointmentStartDate && value && this.oldValue.appointmentStartDate !== value.appointmentStartDate) {
        this.oldValue = value;
        
              let payloadAvailability:IAvail = value;
              const timeslots = this.availability.availableDates
              ?.find(a => a.date === value.appointmentStartDate)
              ?.timeSlots;
              const openTimeSlots = timeslots?.filter(data => data.isOpen);
              if(openTimeSlots?.length > 0) {
                payloadAvailability = {...payloadAvailability, timeSlotId: openTimeSlots[0].timeSlotId};
              } else {
                payloadAvailability = {...payloadAvailability, timeSlotId: ""};
              }

              /* *this condition is needed to handle inside availability day screen, if we move from closed day to next closed day */
              if(this.availability?.offLeaveDays?.find(data=>data.leaveDate === this.value.appointmentStartDate) && this.availability.offLeaveDays.find(data=>data.leaveDate === this.oldValue.appointmentStartDate)) {
                payloadAvailability = {...payloadAvailability, timeSlotId: value.timeSlotId};
              }

              this.form.patchValue(payloadAvailability);
              this.setDisabledTimeSlot();
      }

      //PHASE-2: This logic is to fetch advisor based availability
      // if( this.source!== 'dashboard') {
        if (this.options.department === Department.SERVICE && value.advisorId && this.oldValue.advisorId
          && this.oldValue.advisorId !== value.advisorId) {
            const formDate = this.currentMonth ? this.currentMonth : (value.appointmentStartDate? DateTime.fromISO(value.appointmentStartDate) : DateTime.now());
            const date = DateTime.fromISO(formDate.toString())
            .startOf('month')
            .toFormat(this.dateFormat);
          this.serviceAppointmentStore.dispatch(new LoadAvailability({ date: date, advisorId: value.advisorId || -1,
             transport: this.selectedTransport, vin: this.vin, appointmentId: this.appointmentId }));
        } 
        else if (this.options.department === Department.SALES && value.advisorId && (
          (this.oldValue.advisorId && this.oldValue.advisorId !== value.advisorId) ||
          (this.apptType === AppointmentType.DELIVERY && this.oldValue.fniAdvisorId !== value.fniAdvisorId)
        )) {
            const formDate = this.currentMonth ? this.currentMonth : (value.appointmentStartDate? DateTime.fromISO(value.appointmentStartDate) : DateTime.now());
            const date = DateTime.fromISO(formDate.toString())
            .startOf('month')
            .toFormat(this.dateFormat);
        /* *availability loads differently for testdrive appts, vehicles with a vin or without a vin */
        this.setNewAvailability(date, value, this.override);
      }
      if(this.override.allow) {
        const date =  DateTime.fromISO(this.override.date)
        .startOf('month')
        .toFormat(this.dateFormat);

        /* *override Availability --->>> availability loads differently for testdrive appts, vehicles with a vin or without a vin */
      
        this.setNewAvailability(date, value, this.override);
      }

      if(value.advisorId === '-1') {
        this.addLeaveService.resetValues();
        this.closedDates = [];
      }

      if (this.options.department === Department.SALES && this.oldValue.timeSlotId !== value.timeSlotId) {
        this.checkOverrideOperationalHours(value);
      }

      this.oldValue = value;
      this.formEdited.emit(!this.form.dirty);
    });
  }

  setNewAvailability(date, value, override) {
    if (this.apptType === AppointmentType.TEST_DRIVE && this.vin){
        const payload = { selectDateAndSlot: false,
          date: date, 
          advisorId: value.advisorId || -1, 
          transport: this.selectedTransport, 
          vin: this.vin, 
          appointmentType: AppointmentType.TEST_DRIVE,
          appointmentId: this.appointmentId 
        };
        if(override.allow) {
          payload['override'] = override;
        }
      this.dispatchSalesLikeAvailability(payload);
    } 
    if(this.apptType === AppointmentType.DELIVERY) {
    let payload = {selectDateAndSlot: false,
      date: date, advisorId: value.advisorId || -1, transport: this.selectedTransport,
      fniAdvisorId: value.fniAdvisorId || '-1',
      vin: this.vin , appointmentType:this.apptType, appointmentId: this.appointmentId };
      if(this.apptType === AppointmentType.DELIVERY) {
        payload['selectDateAndSlot'] = false;
      }
      if(override.allow) {
        payload['override'] = override;
      }
      if (this.selectedTransport && !this.disableCalendar) {
        this.dispatchSalesLikeAvailability(payload);
      };
    }
    if (this.apptType === AppointmentType.PARTS) {
      const payload = {
        selectDateAndSlot: false,
        date: date,
        advisorId: value.advisorId || -1,
        transport: this.selectedTransport,
        appointmentType: AppointmentType.PARTS,
        appointmentId: this.appointmentId
      };
      if (override.allow) {
        payload['override'] = override;
      }
      this.dispatchSalesLikeAvailability(payload);
    }
    if (this.apptType === AppointmentType.LEARNING) {
      const payload = {
        selectDateAndSlot: false,
        date: date, 
        advisorId: value.advisorId || -1, 
        transport: this.selectedTransport, 
        appointmentType: AppointmentType.LEARNING, 
        appointmentId: this.appointmentId 
      };
      if(override.allow) {
        payload['override'] = override;
      }
      this.dispatchSalesLikeAvailability(payload);
    } 
    if(this.apptType === AppointmentType.VEHICLE_INQUIRY) {
      const payload = { 
        selectDateAndSlot: false,
        date: date, 
        advisorId: value.advisorId || -1, 
        transport: this.selectedTransport, 
        appointmentType: AppointmentType.VEHICLE_INQUIRY, 
        appointmentId: this.appointmentId 
      };
      if(override.allow) {
        payload['override'] = override;
      }
      this.dispatchSalesLikeAvailability(payload);
    }
  }

  isDashBoard() {
    return this.source!== 'dashboard'? false: true;  
  }

  private normalizeAvailabilityValue(value: AvailabilityFormValues): AvailabilityFormValues {
    return {
      advisorId: value?.advisorId || '-1',
      fniAdvisorId: value?.fniAdvisorId || '-1',
      languageId: value?.languageId || '',
      timeSlotId: value?.timeSlotId || '',
      appointmentStartDate: value?.appointmentStartDate || ''
    };
  }

  private getSalesAdvisorOptions(languageId?: string, remoteOnly = false): IEmployee[] {
    let advisors = [...(this.options?.advisors || [])];

    if (this.options?.department === Department.SALES && this.apptType === AppointmentType.DELIVERY) {
      advisors = advisors.filter(advisor => advisor.id === '-1' || !advisor.finance);
    }

    if (languageId) {
      advisors = advisors.filter(advisor => advisor.languages && advisor.languages.some(language => language.languageId === languageId));
    }

    if (remoteOnly) {
      advisors = advisors.filter(advisor => advisor.remoteConsultant || advisor.id === '-1');
    }

    return advisors;
  }

  private getFniAdvisorOptions(): IEmployee[] {
    const fniAdvisors = ((this.options as any)?.fniAdvisors || []) as IEmployee[];
    if (fniAdvisors.length) {
      return fniAdvisors;
    }

    const advisors = this.options?.advisors || [];
    const anyAdvisor = advisors.find(advisor => advisor.id === '-1');
    const financeAdvisors = advisors.filter(advisor => advisor.finance);

    if (!financeAdvisors.length) {
      return [];
    }

    if (!anyAdvisor) {
      return financeAdvisors;
    }

    return [
      { ...anyAdvisor, displayName: 'Any F&I Manager' },
      ...financeAdvisors.filter(advisor => advisor.id !== '-1')
    ];
  }

  checkOverrideOperationalHours(value: any) {
    if ((this.oldValue.timeSlotId || value.timeSlotId) && (this.settings?.time) && !this.disableCalendar) {
      let payloadAvailability = { ...value };
      const hoursOfOperation = this.settings.time.hoursOfOperation;
      const day = DateTime.fromISO(value.appointmentStartDate).weekday;
      const time = DateTime.fromFormat(hoursOfOperation[day - 1].endTime, 'h:mm a');
      const duration = time.diff(DateTime.fromFormat(value.timeSlotId, 'hh:mm a'), 'minutes').minutes;
      let appointmentDuration;
      if (this.apptType === AppointmentType.DELIVERY) {
        appointmentDuration = this.settings.time.deliveryAppointmentDuration + this.settings.time.deliveryApptDuration;
      } else if (this.apptType === AppointmentType.TEST_DRIVE) {
        appointmentDuration = this.settings.time.testdriveAppointmentDuration;
      } else {
        appointmentDuration = this.settings.time.techcenterAppointmentDuration;
      }
      if (this.weekdays[day - 1].toLowerCase() === hoursOfOperation[day - 1].day.toLowerCase() && appointmentDuration > duration && !this.form.pristine) {

        if(this.timerid !== null){
          clearTimeout(this.timerid);
        }
        this.timerid  = setTimeout(() => 
        {
          this.confirmConfig.data.clicked = true;
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {...this.confirmConfig});
          this.subscriptions.dialogRef = dialogRef.afterClosed().subscribe(event => {
           if (event.action === 'close' || event.action === 'closeWindow') {
            payloadAvailability = { ...value, timeSlotId: '' };
            this.form.patchValue(payloadAvailability);
            }
          })
          this.timerid = null;
        }, 400);
      }
    }
  }

  getNotification(event) {
    /* *override availability if event received as confirm */
    if(event.action === 'confirm') {   
      this.override = {allow: true, date: event.data.overrideSelectedDate};
      this.oldValue =  {...this.oldValue, appointmentStartDate: ''};
      const payloadAvailability = { ...this.oldValue, appointmentStartDate : this.override.date };
      this.form.patchValue(payloadAvailability);
    }
  }

  onMonthChange($event: DateTime) {
    this.currentMonth = $event;
    this.monthChanged.emit($event);
  }
  setDisabledTimeSlot() {
    const availableTimeSlotDates = this.availability.availableDates?.filter(data => data.isOpen);
    /*
    if (availableTimeSlotDates?.find(a => a.date === this.form.value.appointmentStartDate)?.timeSlots === undefined) {
      this.isTimeSlotDisabled = false;
    }
    else {
      this.isTimeSlotDisabled = true;
    }
    */
    this.isTimeSlotDisabled = true;
    const timeslots = availableTimeSlotDates?.find(a => a.date === this.form.value.appointmentStartDate)?.timeSlots;
    if (timeslots?.length > 0 && timeslots?.find(time => time.timeSlotId === this.form.value.timeSlotId)?.isOpen) {
      this.timeSlotValidator.emit(!this.isTimeSlotDisabled);
    }
    else {
      this.timeSlotValidator.emit(true)
    }
    if(this.options.department === Department.SALES ) {
      this.checkOverrideOperationalHours(this.form.value);
    }
  }
  emitTimeSlotValidator(event: boolean) {
    this.timeSlotValidator.emit(event)
  }
  
  emitDisableOverride(event: any) {
    if(event) {
      this.override = {allow: false, date: ''};
    }
  }

  getMonth(month) {
    return month ? this.translate.instant(`common.${month}`) : month;
  }
  getLanguage(language) {
    return language ? this.translate.instant(`common.${language}`) : language;
  }

  updateAdvisorOnLanguageChange(advisorsList, value: IAvail) {
    let advisorId = value.advisorId;
    const advisors = advisorsList
      ?.filter(a => a?.languages && a?.languages.length ? a?.languages?.some(l => l.languageId === value.languageId) : []);
    if (advisors && !advisors.find(x => x.id === advisorId)) {
      advisorId = '-1';
    }
    if(advisorId !== value.advisorId) {
    this.selected = advisorId;
    this.form.get('advisorId').patchValue(advisorId);
    }
  }

}
